<template>
  <FsDialog v-model="dialogVisible" :title="t('components/feedbackPopup.title')" width="480px">
    <template v-if="!success">
      <FsForm class="feedback_form" :model="model" :rules="rules" ref="formRef" horizontal>
        <FsFormItem prop="topic" :label="t('components/feedbackPopup.topicLabel')">
          <FsSelect new-regular v-model="model.topic" :options="options" />
        </FsFormItem>
        <FsFormItem prop="description" :label="t('components/feedbackPopup.descriptionLabel')">
          <FsInput
            new-regular
            v-model="model.description"
            :maxlength="1000"
            :show-number="true"
            type="textarea"
            :placeholder="t('components/feedbackPopup.descriptionPlaceholder')"
          >
          </FsInput>
        </FsFormItem>
        <p class="info">{{ t('components/feedbackPopup.contactInfo') }}</p>
        <FsFormItem prop="name">
          <FsInput new-regular :placeholder="t('components/feedbackPopup.namePlaceholder')" v-model="model.name" />
        </FsFormItem>
        <FsFormItem prop="email">
          <FsInput new-regular :placeholder="t('components/feedbackPopup.emailPlaceholder')" v-model="model.email" />
        </FsFormItem>
      </FsForm>
    </template>

    <div class="success_box" v-else>
      <div class="success_icon">
        <i class="iconfont">&#xe710;</i>
      </div>
      <div class="success_tit">{{ t('components/feedbackPopup.successTitle') }}</div>
      <p class="success_txt">{{ t('components/feedbackPopup.successText') }}</p>
    </div>
    <template #footer v-if="!success">
      <FsButton @click.prevent="submit" size="medium" type="red" :loading="loading">
        {{ t('components/common.submit') }}
      </FsButton>
    </template>
  </FsDialog>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch, nextTick } from 'vue'

import { feedback } from '@/api/modules/user'
import { useUserStore } from '@/store/user'
import { storeToRefs } from 'pinia'
import { useI18n } from '@/hooks/useI18n'

const userStore = useUserStore()
const { t } = useI18n()

const { customersEmailAddress, customersFirstname, customersLastname } = storeToRefs(userStore)

const dialogVisible = ref<boolean>(false)
const options = computed(() => [
  { label: t('components/feedbackPopup.topicWebsiteDesign'), value: 'Website Design' },
  { label: t('components/feedbackPopup.topicSearchAndNavigation'), value: 'Search and Navigation' },
  { label: t('components/feedbackPopup.topicProduct'), value: 'Product' },
  { label: t('components/feedbackPopup.topicCheckoutAndPayment'), value: 'Checkout and Payment' },
  { label: t('components/feedbackPopup.topicShippingAndDelivery'), value: 'Shipping and Delivery' },
  { label: t('components/feedbackPopup.topicReturnAndExchange'), value: 'Return and Exchange' },
  { label: t('components/feedbackPopup.topicServiceAndSupport'), value: 'Service and Support' },
  { label: t('components/feedbackPopup.topicWebsiteSuggestion'), value: 'Website Suggestion' },
  { label: t('components/feedbackPopup.topicOther'), value: 'Other' },
])

const model = reactive({
  topic: 'Website Design',
  description: '',
  name: '',
  email: '',
})

const success = ref<boolean>(false)
const loading = ref<boolean>(false)

const formRef = ref()

const getDefaultModel = () => ({
  topic: 'Website Design',
  description: '',
  name: `${customersFirstname.value || ''} ${customersLastname.value || ''}`.trim(),
  email: `${customersEmailAddress.value || ''}`,
})

const resetFeedbackState = () => {
  success.value = false
  loading.value = false
  Object.assign(model, getDefaultModel())
  nextTick(() => {
    formRef.value?.clearValidate?.()
  })
}

const rules = computed(() => ({
  topic: [
    {
      type: 'string',
      required: true,
      trigger: 'change',
      message: t('components/feedbackPopup.topicRequired'),
    },
  ],
  description: [
    {
      type: 'string',
      required: true,
      trigger: 'change',
      message: t('components/feedbackPopup.descriptionMin'),
      min: 10,
    },
  ],
  name: [
    {
      type: 'string',
      required: true,
      trigger: 'blur',
      message: t('components/feedbackPopup.nameRequired'),
    },
  ],
  email: [
    {
      type: 'string',
      required: true,
      trigger: 'blur',
      message: t('components/feedbackPopup.emailRequired'),
    },
  ],
}))

const submit = async () => {
  try {
    await formRef.value.validate()
    loading.value = true
    await feedback({
      topic: model.topic,
      content: model.description,
      name: model.name,
      email: model.email,
    })
    success.value = true
  } catch (e) {
    console.log('the promise', e)
  } finally {
    loading.value = false
  }
}
const showDialog = () => {
  dialogVisible.value = true
}

const hideDialog = () => {
  dialogVisible.value = false
}

watch(
  [customersEmailAddress, customersFirstname, customersLastname],
  () => {
    if (!dialogVisible.value || !success.value) {
      Object.assign(model, getDefaultModel())
    }
  },
  { immediate: true }
)

watch(dialogVisible, visible => {
  if (!visible) {
    resetFeedbackState()
  }
})

defineExpose({
  showDialog,
  hideDialog,
})
</script>

<style lang="scss" scoped>
.feedback_form {
  :deep(.fs-textarea__number) {
    // display: none;
  }
}

.info {
  margin-bottom: 4px;
  color: #707070;
  @include font12;
}

.btn_box {
  margin-top: 20px;

  :deep(.fs-form__item--content) {
    display: flex;
    flex-direction: row-reverse;

    .fs-button {
      margin-left: 16px;
    }
  }
}

.success_box {
  margin: 0 auto;
  padding: 40px 0 100px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .success_icon {
    margin-bottom: 16px;
    display: flex;
    justify-content: center;

    .iconfont {
      font-size: 50px;
      width: 50px;
      height: 50px;
      color: #10a300;
    }
  }

  .success_tit {
    @include font16;
    font-weight: 600;
    text-align: center;
    color: #19191a;
  }

  .success_txt {
    font-size: 16px;
    color: #19191a;
    text-align: center;
    font-weight: 600;
    max-width: 300px;
  }
}

:deep(.fs-form__item:last-child) {
  margin-bottom: 0;
}
</style>
